import DashboardLayout from "@/layouts/dashboard";
import PageError from "@/pages/sys/error/PageError";
import ProtectedRoute from "@/router/components/protected-route";
import { usePermissionRoutes } from "@/router/hooks";
import { ERROR_ROUTE } from "@/router/routes/error-routes";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, type RouteObject, createHashRouter, Outlet } from "react-router";
import { RouterProvider } from "react-router/dom";
import type { AppRouteObject } from "#/router";
import RegisterForm from "@/pages/sys/login/RegisterForm";
import NewLoginPage from "@/pages/sys/login/NewLoginPage";
import NewSignUpPage from "@/pages/sys/login/NewSignUpPage";
import ClientPreFilledSignUpPage from "@/pages/sys/login/ClientPreFilledSignUpPage";
import EnterpriseSignUpPage from "@/pages/sys/login/EnterpriseSignUpPage";
import NewEmailVerifyPage from "@/pages/sys/login/NewEmailVerifyPage";
import ForgotPasswordPage from "@/pages/sys/login/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/sys/ResetPasswordPage";
import EnterprisePaymentPromptPage from "@/pages/sys/login/EnterprisePaymentPromptPage";
import PageTitleUpdater from "@/router/components/page-title-updater";
import EditorPrefilledSignUpPage from "@/pages/sys/login/EditorPrefilledSignupPage";

const HOMEPAGE = import.meta.env.VITE_APP_HOMEPAGE || "/dashboard/home";

function RootLayout() {
	return (
		<>
			<PageTitleUpdater />
			<Outlet />
		</>
	);
}

const PUBLIC_ROUTE: AppRouteObject = {
	path: "/login",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<NewLoginPage />
		</ErrorBoundary>
	),
};

const NEW_PUBLIC_ROUTE: AppRouteObject = {
	path: "/new-login",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<NewLoginPage />
		</ErrorBoundary>
	),
};

const PUBLIC_REGISTER_ROUTE: AppRouteObject = {
	path: "/register-client",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<RegisterForm />
		</ErrorBoundary>
	),
};

const PUBLIC_SIGNUP_ROUTE: AppRouteObject = {
	path: "/sign-up-user",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<NewSignUpPage />
		</ErrorBoundary>
	),
};

const PUBLIC_CLIENT_SIGNUP_ROUTE: AppRouteObject = {
	path: "/sign-up-client",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<ClientPreFilledSignUpPage />
		</ErrorBoundary>
	),
};

const PUBLIC_EDITOR_SIGNUP_ROUTE: AppRouteObject = {
	path: "/sign-up-client-user",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<EditorPrefilledSignUpPage />
		</ErrorBoundary>
	),
};

const PUBLIC_ENTERPRISE_SIGNUP_ROUTE: AppRouteObject = {
	path: "/sign-up-enterprise",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<EnterpriseSignUpPage />
		</ErrorBoundary>
	),
};

const PUBLIC_ENTERPRISE_PAYMENT_PROMPT_ROUTE: AppRouteObject = {
	path: "/enterprise-payment-prompt",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<EnterprisePaymentPromptPage />
		</ErrorBoundary>
	),
};

const PUBLIC_FORGOT_PASSWORD_ROUTE: AppRouteObject = {
	path: "/forgot-password",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<ForgotPasswordPage />
		</ErrorBoundary>
	),
};

const PUBLIC_RESET_FORGOT_PASSWORD_ROUTE: AppRouteObject = {
	path: "/reset-password",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<ResetPasswordPage />
		</ErrorBoundary>
	),
};

const PUBLIC_EMAIL_VERIFY_ROUTE: AppRouteObject = {
	path: "/verify-user-email",
	element: (
		<ErrorBoundary FallbackComponent={PageError}>
			<NewEmailVerifyPage />
		</ErrorBoundary>
	),
};

const NO_MATCHED_ROUTE: AppRouteObject = {
	path: "*",
	element: <Navigate to="/404" replace />,
};

export default function Router() {
	const permissionRoutes = usePermissionRoutes();

	const PROTECTED_ROUTE: AppRouteObject = {
		path: "/",
		element: (
			<ProtectedRoute>
				<DashboardLayout />
			</ProtectedRoute>
		),
		children: [
			{ index: true, element: <Navigate to={HOMEPAGE} replace /> },
			...permissionRoutes,
		],
	};

	const routes = [
		{
			element: <RootLayout />,
			children: [
				PUBLIC_ROUTE,
				PUBLIC_REGISTER_ROUTE,
				PUBLIC_SIGNUP_ROUTE,
				 PUBLIC_CLIENT_SIGNUP_ROUTE,
				 PUBLIC_EDITOR_SIGNUP_ROUTE,
				PUBLIC_ENTERPRISE_SIGNUP_ROUTE,
				PUBLIC_ENTERPRISE_PAYMENT_PROMPT_ROUTE,
				PUBLIC_FORGOT_PASSWORD_ROUTE,
				PUBLIC_EMAIL_VERIFY_ROUTE,
				PROTECTED_ROUTE,
				ERROR_ROUTE,
				NO_MATCHED_ROUTE,
				NEW_PUBLIC_ROUTE,
				PUBLIC_RESET_FORGOT_PASSWORD_ROUTE,
			],
		},
	] as RouteObject[];

	const router = createHashRouter(routes);

	return <RouterProvider router={router} />;
}
